import type { Layout, LayoutItem } from "react-grid-layout";

export const collides = (sourceItem: LayoutItem, targetItem: LayoutItem) =>
  sourceItem.i !== targetItem.i &&
  sourceItem.x < targetItem.x + targetItem.w &&
  sourceItem.x + sourceItem.w > targetItem.x &&
  sourceItem.y < targetItem.y + targetItem.h &&
  sourceItem.y + sourceItem.h > targetItem.y;

export const sortLayoutItems = (left: LayoutItem, right: LayoutItem) =>
  left.y - right.y || left.x - right.x || left.i.localeCompare(right.i);

export const resolveLayoutOverlaps = (
  nextLayout: Layout,
  changedItemId: string | null,
): Layout => {
  if (!changedItemId) {
    return nextLayout;
  }

  const layoutById = new Map<string, LayoutItem>(
    nextLayout.map((item) => [item.i, { ...item }]),
  );

  if (!layoutById.has(changedItemId)) {
    return nextLayout;
  }

  const queue = [changedItemId];
  const queuedItemIds = new Set(queue);
  const iterationLimit = nextLayout.length * nextLayout.length;
  let iterations = 0;

  while (queue.length > 0 && iterations < iterationLimit) {
    const currentItemId = queue.shift();

    if (!currentItemId) {
      break;
    }

    queuedItemIds.delete(currentItemId);
    const sourceItem = layoutById.get(currentItemId);

    if (!sourceItem) {
      continue;
    }

    Array.from(layoutById.values())
      .filter((targetItem) => collides(sourceItem, targetItem))
      .sort(sortLayoutItems)
      .forEach((targetItem) => {
        const nextY = Math.max(targetItem.y, sourceItem.y + sourceItem.h);

        if (nextY === targetItem.y) {
          return;
        }

        layoutById.set(targetItem.i, {
          ...targetItem,
          y: nextY,
        });

        if (!queuedItemIds.has(targetItem.i)) {
          queue.push(targetItem.i);
          queuedItemIds.add(targetItem.i);
        }
      });

    iterations += 1;
  }

  return Array.from(layoutById.values()).sort(sortLayoutItems);
};

export const getSwapTargetId = (
  originalLayout: Layout,
  draggedItem: LayoutItem | null | undefined,
): string | null => {
  if (!draggedItem) {
    return null;
  }

  const draggedCenterX = draggedItem.x + draggedItem.w / 2;
  const draggedCenterY = draggedItem.y + draggedItem.h / 2;

  const targetItem =
    originalLayout.find((item) => {
      if (item.i === draggedItem.i) {
        return false;
      }

      return (
        draggedCenterX >= item.x &&
        draggedCenterX <= item.x + item.w &&
        draggedCenterY >= item.y &&
        draggedCenterY <= item.y + item.h
      );
    }) ?? originalLayout.find((item) => collides(draggedItem, item));

  return targetItem?.i ?? null;
};

const getHorizontalColumnTarget = (
  originalLayout: Layout,
  draggedItem: LayoutItem,
): LayoutItem | null => {
  const draggedCenterX = draggedItem.x + draggedItem.w / 2;

  const matchingColumns = originalLayout
    .filter((item) => item.i !== draggedItem.i)
    .filter(
      (item) =>
        draggedCenterX >= item.x && draggedCenterX <= item.x + item.w,
    )
    .sort((left, right) => {
      const leftDistance = Math.abs(
        draggedCenterX - (left.x + left.w / 2),
      );
      const rightDistance = Math.abs(
        draggedCenterX - (right.x + right.w / 2),
      );

      return (
        leftDistance - rightDistance ||
        left.y - right.y ||
        left.x - right.x ||
        left.i.localeCompare(right.i)
      );
    });

  return matchingColumns[0] ?? null;
};

const overlapsHorizontally = (
  sourceItem: LayoutItem,
  targetX: number,
  targetWidth: number,
) =>
  sourceItem.x < targetX + targetWidth &&
  sourceItem.x + sourceItem.w > targetX;

export const buildColumnDropLayout = (
  originalLayout: Layout,
  nextLayout: Layout,
  draggedItemId: string | null | undefined,
): Layout | null => {
  if (!draggedItemId) {
    return null;
  }

  const originalDraggedItem =
    originalLayout.find((item) => item.i === draggedItemId) ?? null;
  const currentDraggedItem =
    nextLayout.find((item) => item.i === draggedItemId) ?? null;

  if (!originalDraggedItem || !currentDraggedItem) {
    return null;
  }

  if (originalDraggedItem.x === currentDraggedItem.x) {
    return null;
  }

  const targetItemId = getSwapTargetId(originalLayout, currentDraggedItem);
  const originalTargetItem = targetItemId
    ? (originalLayout.find((item) => item.i === targetItemId) ?? null)
    : null;
  const horizontalColumnTarget = getHorizontalColumnTarget(
    originalLayout,
    currentDraggedItem,
  );
  const destinationTarget = originalTargetItem ?? horizontalColumnTarget;
  const destinationX = destinationTarget?.x ?? currentDraggedItem.x;
  const destinationWidth = destinationTarget?.w ?? currentDraggedItem.w;
  const droppedY = currentDraggedItem.y;
  const layoutById = new Map<string, LayoutItem>(
    nextLayout.map((item) => [item.i, { ...item }]),
  );
  const latestDraggedItem = layoutById.get(draggedItemId);

  if (!latestDraggedItem) {
    return null;
  }

  layoutById.set(draggedItemId, {
    ...latestDraggedItem,
    x: destinationX,
    y: droppedY,
    w: destinationWidth,
  });

  Array.from(layoutById.values())
    .filter(
      (item) =>
        item.i !== draggedItemId &&
        item.y >= droppedY &&
        overlapsHorizontally(item, destinationX, destinationWidth),
    )
    .sort(sortLayoutItems)
    .forEach((item) => {
      layoutById.set(item.i, {
        ...item,
        y: item.y + latestDraggedItem.h,
      });
    });

  return resolveLayoutOverlaps(
    Array.from(layoutById.values()).sort(sortLayoutItems),
    draggedItemId,
  );
};

const areItemsInSameRow = (left: LayoutItem, right: LayoutItem) =>
  left.y === right.y;

export const buildPushedDropLayout = (
  originalLayout: Layout,
  nextLayout: Layout,
  draggedItemId: string | null | undefined,
): Layout | null => {
  if (!draggedItemId) {
    return null;
  }

  const currentDraggedItem =
    nextLayout.find((item) => item.i === draggedItemId) ?? null;

  if (!currentDraggedItem) {
    return null;
  }

  const targetItemId = getSwapTargetId(originalLayout, currentDraggedItem);

  if (!targetItemId) {
    return null;
  }

  const originalDraggedItem =
    originalLayout.find((item) => item.i === draggedItemId) ?? null;
  const originalTargetItem =
    originalLayout.find((item) => item.i === targetItemId) ?? null;

  if (
    !originalDraggedItem ||
    !originalTargetItem ||
    !areItemsInSameRow(originalDraggedItem, originalTargetItem)
  ) {
    return null;
  }

  const layoutById = new Map<string, LayoutItem>(
    nextLayout.map((item) => [item.i, { ...item }]),
  );
  const currentTargetItem = layoutById.get(targetItemId);

  if (!currentTargetItem) {
    return null;
  }

  layoutById.set(draggedItemId, {
    ...currentDraggedItem,
    x: originalTargetItem.x,
    y: originalTargetItem.y,
  });

  layoutById.set(targetItemId, {
    ...currentTargetItem,
    x: originalTargetItem.x,
    y: originalTargetItem.y + currentDraggedItem.h,
  });

  const layoutWithDraggedWidgetPlaced = resolveLayoutOverlaps(
    Array.from(layoutById.values()).sort(sortLayoutItems),
    draggedItemId,
  );

  return resolveLayoutOverlaps(layoutWithDraggedWidgetPlaced, targetItemId);
};
